import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { CCP_SEARCH_MAX_LIMIT, maxThresholdForResults, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { useFeatureToggle } from '@css-xp/base';
import { Currency } from '@css-xp/ppc-common';
import { getDataForStore } from '../store-utils';
import { OperationType } from '../../../../types';
import { truthy } from '../../../../utils';
import { fetchBillingProfilesForCustomer } from '../../../../queries/fetch-billing-profiles-for-customer';
import { useLoadEntitlements } from './use-load-entitlements';
import { useCloudSiteOrOrgNameMapStore, useOfferingsAndPricingPlansStore, useStore } from '../../../../store';
import { commonEntitlementQueryFilters } from '../common-entitlement-query-filters';
import { fetchCloudSiteProductsBelongingToOtherTxa } from '../api';
import { ExternalProductListFetchStatus, ExternalProductsList } from '../types';

export const useBillingProfileAndEntitlements = ({
  customer,
  invoiceGroupId,
  isDesignIteration1Enabled,
}: {
  customer?: { name: string; ids: string[] } | null;
  invoiceGroupId?: string;
  isDesignIteration1Enabled: boolean;
}) => {
  const customerIds = customer?.ids;
  const { txaId } = useParams<{ txaId: string }>();
  const [{ featureFlags }] = useStore();
  const [, actions] = useCloudSiteOrOrgNameMapStore();
  const offeringsAndPricingPlans = useOfferingsAndPricingPlansStore();

  const [isEnabled] = useFeatureToggle();
  const isEntitlementFetchInChunksEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ENTITLEMENT_FETCH_IN_CHUNKS_ON_UOF);
  const showInactiveProducts = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_INACTIVE_PRODUCTS_IN_UOF] || false;
  const showAddons = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS] || false;
  const isNonCommercialChangesEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING] || false;
  const isMultiCurrencyChangesEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES] || false;
  const isFetchBillingProfilesViaCCPEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_FROM_CCP);
  const showSandboxProducts = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS] || false;
  const isSandboxDecouplingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING] || false;
  const isFutureDatedEntitlementEnabled =
    featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS] || false;

  const isTWCPhase1Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1] || false;
  const isTWCPhase2Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2] || false;
  const isAddOnsPhase2Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2] || false;
  const isMultiInstanceAppsEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS] || false;

  const [externalProductsList, setExternalProductsList] = useState<ExternalProductsList>([]);
  const [externalProductListFetchStatus, setExternalProductListFetchStatus] = useState<ExternalProductListFetchStatus>(
    ExternalProductListFetchStatus.IDLE
  );

  const isExternalProductListLoading = externalProductListFetchStatus === ExternalProductListFetchStatus.PENDING;

  const shouldSkipBillingProfileDetailsFetch =
    !txaId || !customerIds || !customerIds.length || !isDesignIteration1Enabled;

  const {
    data: billingProfileDetails,
    loading: isBillingProfileDetailsLoading,
    error: billingProfileError,
    refetch: refetchBillingProfileDetails,
  } = useQuery<
    generatedTypesNew.FetchBillingProfilesForCustomerQuery,
    generatedTypesNew.FetchBillingProfilesForCustomerQueryVariables
  >(fetchBillingProfilesForCustomer, {
    variables: {
      id: txaId,
      searchOptions: {
        first: isFetchBillingProfilesViaCCPEnabled ? CCP_SEARCH_MAX_LIMIT : maxThresholdForResults,
        offset: 0,
      },
      filterOptions: [
        {
          key: 'or',
          value: JSON.stringify(customerIds?.map((customerId: string) => ({ shipToId: customerId }))),
        },
      ],
      isFetchBillingProfilesViaCCPEnabled,
    },
    skip: shouldSkipBillingProfileDetailsFetch,
  });

  const billingProfilesListForCustomer = isFetchBillingProfilesViaCCPEnabled
    ? billingProfileDetails?.transactionAccount?.billingProfilesFromCCP?.nodes
    : billingProfileDetails?.transactionAccount?.billingProfiles?.nodes;

  const billingProfileResponse = useMemo(() => {
    let filteredBillingProfile =
      billingProfilesListForCustomer
        ?.map((billingProfile) => {
          return {
            ...billingProfile,
            shipToPriceEligibility: billingProfile?.shipToPriceEligibility?.toUpperCase() ?? CCPPricing.COMMERCIAL,
            currency: billingProfile?.currency?.toUpperCase() ?? Currency.USD,
          };
        })
        .filter((billingProfile: any) =>
          showInactiveProducts ? true : billingProfile?.defaultPaymentMethod?.type !== 'DEFERRED'
        )
        .filter(truthy) ?? [];

    const selectedBillingProfile = filteredBillingProfile.find((profile: any) => profile.id === invoiceGroupId);

    if (isNonCommercialChangesEnabled) {
      filteredBillingProfile = filteredBillingProfile.filter(
        (billingProfile) =>
          billingProfile?.shipToPriceEligibility.toUpperCase() ===
          selectedBillingProfile?.shipToPriceEligibility?.toUpperCase()
      );
    }

    if (isMultiCurrencyChangesEnabled) {
      filteredBillingProfile = filteredBillingProfile.filter(
        (billingProfile: any) =>
          billingProfile.currency.toUpperCase() === selectedBillingProfile?.currency?.toUpperCase()
      );
    }

    return filteredBillingProfile?.filter((billingProfile) => billingProfile?.isActive === true);
  }, [billingProfilesListForCustomer, invoiceGroupId]);

  const invoiceGroupIdsForFilter = useMemo(() => {
    return billingProfileResponse.map(({ id }) => id).filter(truthy);
  }, [billingProfileResponse]);

  const {
    loading: isFetchEntitlementListLoading,
    error: fetchEntitlementListError,
    data: fetchEntitlementListResponse,
    refetch: refetchEntitlementsResponse,
  } = useLoadEntitlements(
    {
      id: txaId,
      filterOptions: [
        {
          key: 'invoiceGroupIdIsIn',
          values: invoiceGroupIdsForFilter,
        },
        ...(commonEntitlementQueryFilters(true, showInactiveProducts, showAddons) || []),
      ],
      searchOptions: {
        first: maxThresholdForResults,
        offset: 0,
      },
      isRestricted: false,
      fetchECCFromIdentity: false,
    },
    {
      isChunkEnabled: isEntitlementFetchInChunksEnabled,
      skip: !invoiceGroupIdsForFilter.length || !isDesignIteration1Enabled,
    }
  );

  useEffect(() => {
    if (fetchEntitlementListResponse) {
      const productSubscriptions = fetchEntitlementListResponse?.transactionAccount?.productSubscriptions;
      const selectedBillingProfile = billingProfilesListForCustomer?.find(
        (profile: any) => profile.id === invoiceGroupId
      );
      const hasAllData = false;
      const productsAndAppsData = getDataForStore({
        entitlementSubscriptionsWrapper: productSubscriptions,
        offeringsAndpricingPlans: offeringsAndPricingPlans,
        operationType: OperationType.OWN,
        hasAllData,
        includeOrgProducts: true,
        showSandboxProducts,
        isSandboxDecouplingEnabled,
        isFutureDatedEntitlementEnabled,
        isNonCommercialPricingEnabled: isNonCommercialChangesEnabled,
        destinationPricingEligibility: selectedBillingProfile?.shipToPriceEligibility || '',
        isTWCPhase1Enabled,
        isTWCPhase2Enabled,
        isAddOnsPhase2Enabled,
        isMultiInstanceAppsEnabled,
      });

      if (productsAndAppsData) {
        actions.addProductsAndApps(productsAndAppsData);

        (async function fetchExternalProductList() {
          try {
            if (isDesignIteration1Enabled) {
              return;
            }
            const cloudSiteIds = Object.values(productsAndAppsData.products).map(({ cloudSiteId }) => cloudSiteId);
            if (cloudSiteIds && cloudSiteIds.length) {
              setExternalProductListFetchStatus(ExternalProductListFetchStatus.PENDING);
              const externalProductListResponse = await fetchCloudSiteProductsBelongingToOtherTxa({
                currentTransactionAccountId: txaId,
                cloudSiteIds,
              });
              setExternalProductsList(externalProductListResponse);
              setExternalProductListFetchStatus(ExternalProductListFetchStatus.SUCCESS);
            }
          } catch (e) {
            setExternalProductListFetchStatus(ExternalProductListFetchStatus.ERROR);
          }
        })();
      }
    }
  }, [
    actions,
    fetchEntitlementListResponse,
    offeringsAndPricingPlans,
    txaId,
    showSandboxProducts,
    isSandboxDecouplingEnabled,
    isFutureDatedEntitlementEnabled,
    isNonCommercialChangesEnabled,
    invoiceGroupId,
    billingProfilesListForCustomer,
    isMultiInstanceAppsEnabled,
  ]);

  return {
    isBillingProfileDetailsLoading,
    billingProfileError,
    refetchBillingProfileDetails,
    fetchEntitlementListError,
    isFetchEntitlementListLoading,
    refetchEntitlementsResponse,
    externalProductsList,
    isExternalProductListLoading,
  };
};
